import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Crown, Sparkles, Star, X } from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";

// Store Lemon Squeezy DooQuest (product 1163698 / variant 1820726, 70 €/an).
const DOOQUEST_PRO_CHECKOUT_URL =
  "https://dooquest.lemonsqueezy.com/checkout/buy/dc507fad-260e-42d7-bb70-0c4b72f30b99";

export default function FormPollsPricing() {
  const { t } = useTranslation("forms");
  const navigate = useNavigate();
  const currentPlan = "FREE" as "ANONYMOUS" | "FREE" | "PRO";
  const hasFormAccess = currentPlan === "PRO";

  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [waiverError, setWaiverError] = useState(false);

  const handleSubscribe = () => {
    if (!waiverAccepted) {
      setWaiverError(true);
      document
        .getElementById("withdrawal-waiver")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setWaiverError(false);
    const consentTimestamp = new Date().toISOString();
    const checkoutUrl = new URL(DOOQUEST_PRO_CHECKOUT_URL);
    checkoutUrl.searchParams.set("checkout[custom][waiver_accepted]", "true");
    checkoutUrl.searchParams.set(
      "checkout[custom][waiver_accepted_at]",
      consentTimestamp,
    );
    window.location.href = checkoutUrl.toString();
  };

  return (
    <div className="flex flex-col min-h-screen-safe bg-[#0a0a0a] text-white">
      <ProductHeader productType="form" showAuth={true} />
      <div className="flex flex-1">
        <ProductSidebar productType="form" />
        <main className="flex-1 p-8">
          <section className="py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h1 className="text-4xl font-extrabold text-white mb-4">
                  {t("pricing.header.title")}
                </h1>
                <p className="text-xl text-gray-300">
                  {t("pricing.header.subtitle")}
                </p>
              </div>

              <div className="flex flex-wrap justify-center items-stretch gap-8 mx-auto max-w-7xl">
                {/* Sans compte */}
                <div className="flex flex-col w-full sm:w-[340px] bg-gradient-to-br from-slate-800/40 to-slate-900/40 rounded-2xl p-8 border border-slate-700/40 shadow-lg hover:shadow-slate-900/20 transition-shadow">
                  <div className="flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-slate-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {t("pricing.anon.name")}
                  </h3>
                  <p className="text-gray-400 mb-4 text-center min-h-[3rem] flex items-center justify-center">
                    {t("pricing.anon.tagline")}
                  </p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-300">
                      0€
                    </span>
                    <span className="text-gray-400 ml-1 text-sm">
                      {t("pricing.per_lifetime")}
                    </span>
                  </div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.feature.unlimited_forms")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.feature.unlimited_responses")}
                        <span className="text-gray-500">*</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.feature.all_questions_logic")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.feature.export_csv")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.feature.ai_taste")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <X className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-500 font-medium tracking-wide line-through">
                        {t("pricing.feature.dashboard_history")}
                      </span>
                    </li>
                  </ul>
                  <button
                    onClick={() => navigate("/create")}
                    className="w-full py-3 px-6 bg-slate-700/40 text-slate-200 rounded-lg font-medium hover:bg-slate-700/60 transition-colors"
                  >
                    {t("pricing.anon.cta")}
                  </button>
                </div>

                {/* Gratuit */}
                <div className="flex flex-col w-full sm:w-[340px] bg-gradient-to-br from-violet-900/20 to-violet-800/20 rounded-2xl p-8 border border-violet-800/30 shadow-lg hover:shadow-violet-900/10 transition-shadow">
                  <div className="flex items-center justify-center mb-4">
                    <Star className="w-8 h-8 text-violet-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {t("pricing.free.name")}
                  </h3>
                  <p className="text-gray-400 mb-4 text-center min-h-[3rem] flex items-center justify-center">
                    {t("pricing.free.tagline")}
                  </p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-violet-400">
                      0€
                    </span>
                    <span className="text-gray-400 ml-1 text-sm">
                      {t("pricing.per_lifetime")}
                    </span>
                  </div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.free.feature_all_anon")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.free.feature_dashboard")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.free.feature_notifs")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.free.feature_history")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <X className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-500 font-medium tracking-wide line-through">
                        {t("pricing.feature.unlimited_ai")}
                      </span>
                    </li>
                  </ul>
                  <button
                    onClick={() => navigate("/create")}
                    className="w-full py-3 px-6 bg-violet-900/30 text-violet-400 rounded-lg font-medium hover:bg-violet-900/50 transition-colors"
                  >
                    {t("pricing.free.cta")}
                  </button>
                </div>

                {/* DooQuest Pro */}
                <div className="flex flex-col w-full sm:w-[340px] bg-gradient-to-br from-purple-900/20 to-purple-800/20 rounded-2xl p-8 border-2 border-purple-500 relative shadow-lg hover:shadow-purple-900/10 transition-shadow">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    {t("pricing.most_popular")}
                  </div>
                  <div className="flex items-center justify-center mb-4">
                    <Crown className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {t("pricing.pro.name")}
                  </h3>
                  <p className="text-gray-400 mb-4 text-center min-h-[3rem] flex items-center justify-center">
                    {t("pricing.pro.tagline")}
                  </p>
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-purple-400">
                      70€
                    </span>
                    <span className="text-gray-300 ml-1">
                      {t("pricing.per_year")}
                    </span>
                  </div>
                  <div className="mb-6">
                    <span className="text-xl font-semibold text-purple-300/80">
                      {t("pricing.pro.monthly_equiv")}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {" "}
                      {t("pricing.pro.monthly_note")}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mb-6">
                    {t("pricing.pro.ai_headline")}
                    <span className="text-gray-500">*</span>
                  </p>
                  <ul className="space-y-4 mb-8 flex-grow">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.pro.feature_all_free")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.feature.unlimited_ai")}
                        <span className="text-gray-500">*</span>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.pro.feature_exports")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.pro.feature_no_branding")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-200 font-medium tracking-wide">
                        {t("pricing.pro.feature_retention")}
                      </span>
                    </li>
                  </ul>
                  <div className="space-y-3">
                    {hasFormAccess ? (
                      <button
                        disabled
                        className="w-full py-3 px-6 bg-purple-900/30 text-purple-300 rounded-lg font-medium disabled:opacity-70"
                        data-testid="pricing-pro-button"
                      >
                        {t("pricing.pro.cta_current")}
                      </button>
                    ) : (
                      <>
                        <label
                          id="withdrawal-waiver"
                          className={`flex items-start gap-2 mb-1 cursor-pointer text-[11px] leading-snug rounded-md px-2 py-1.5 border transition-colors ${
                            waiverError
                              ? "border-red-500/70 bg-red-900/20 text-red-300"
                              : "border-white/5 bg-black/20 text-gray-400"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={waiverAccepted}
                            onChange={(e) => {
                              setWaiverAccepted(e.target.checked);
                              if (e.target.checked) setWaiverError(false);
                            }}
                            className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 accent-purple-500 cursor-pointer"
                            aria-invalid={waiverError}
                          />
                          <span>
                            {t("pricing.waiver.text")}{" "}
                            <Link
                              to="/docs/cg-v"
                              target="_blank"
                              rel="noopener"
                              className="text-purple-400 underline hover:text-purple-300"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {t("pricing.waiver.details")}
                            </Link>
                          </span>
                        </label>
                        <button
                          onClick={handleSubscribe}
                          disabled={!waiverAccepted}
                          className={`w-full py-3 px-6 rounded-lg font-medium transition-all ${
                            waiverAccepted
                              ? "bg-gradient-to-r from-purple-600 to-violet-600 text-white hover:from-purple-500 hover:to-violet-500 cursor-pointer"
                              : "bg-gray-700 text-gray-400 cursor-not-allowed"
                          }`}
                          data-testid="pricing-pro-button"
                          aria-disabled={!waiverAccepted}
                        >
                          {t("pricing.pro.cta_subscribe")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-12 max-w-3xl mx-auto px-6 py-4 rounded-xl border border-white/10 bg-white/[0.02] flex items-center justify-center gap-3 text-sm text-gray-300">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span>
                  <strong className="text-white">
                    {t("pricing.mobile.title")}
                  </strong>{" "}
                  {t("pricing.mobile.text")}
                </span>
              </div>

              <p className="text-center text-xs text-gray-500 mt-10 max-w-2xl mx-auto">
                {t("pricing.fair_use_note")}
              </p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
